import type { ServicesFeedbackType } from '~/components/page/LFA/state/servicesFeedbackState';
import type { Date__Input } from '~/graphql/types';
import formatToastMessage, {
  type FormattedToastMessage,
} from '~/util/formatToastMessage';
import type { State } from '../reducer';
import type { Reporter } from '~/hooks/useErrorReporter';
import type { SetterOrUpdater } from 'recoil';

type Args = {
  state: State;
  hasSnoozedService: boolean;
  errorReporter: Reporter;
  addToast: SetterOrUpdater<Array<FormattedToastMessage>>;
  errorMessage: string;
};

const buildFeedback = ({
  state,
  hasSnoozedService,
  errorReporter,
  addToast,
  errorMessage,
}: Args): ServicesFeedbackType | null => {
  try {
    const feedbackEntries = Array.from(state.serviceFeedback.entries());

    if (hasSnoozedService) {
      return {
        Event_Feedback_Snooze: {
          services: feedbackEntries.map(([serviceId, feedback]) => ({
            id: serviceId,
            version: feedback.serviceVersion,
            acceptance: [
              {
                status: feedback.answer,
                rejectionCriteria: feedback.rejectionCriteria.map(criteria => ({
                  id: criteria.id,
                  reason: criteria.reason ?? '',
                })),
              },
            ],
          })),
          // Allow null for snoozeUntil because it will be caught later by validation
          snoozedUntil: state.snoozeUntil as Date__Input,
          note: state.snoozeNote,
        },
      };
    }

    const processedServices = feedbackEntries.map(([serviceId, feedback]) => ({
      id: serviceId,
      version: feedback.serviceVersion,
      acceptance: [
        {
          status: feedback.answer,
          rejectionCriteria: feedback.rejectionCriteria,
        },
      ],
    }));

    return {
      Event_Feedback_Done: {
        services: processedServices.map(service => ({
          id: service.id,
          version: service.version,
          acceptance: [
            {
              status: service.acceptance[0].status,
              rejectionCriteria: service.acceptance[0].rejectionCriteria.map(
                criteria => ({
                  id: criteria.id,
                  reason: criteria.reason ?? '',
                }),
              ),
            },
          ],
        })),
        ...(state.additionalComments.trim() && {
          note: state.additionalComments.trim(),
        }),
      },
    };
  } catch (error) {
    errorReporter.captureException(error as Error, 'error');
    addToast([formatToastMessage(errorMessage, 'danger')]);
    return null;
  }
};

export default buildFeedback;
